/* Photoshoot archive — sections/photoshoot.liquid + sections/photoshoot-index.liquid */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pad2 = (n) => String(n).padStart(2, '0');

  if (!customElements.get('photoshoot-strip')) {
    class PhotoshootStrip extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.pshoot__track');
        if (!this.track) return;
        this.items = Array.from(this.track.children);
        this.prev = this.querySelector('.pshoot__arrow--prev');
        this.next = this.querySelector('.pshoot__arrow--next');
        this.current = this.querySelector('[data-current]');

        this.onScroll = () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => {
            this.raf = null;
            this.update();
          });
        };
        this.onClick = (event) => {
          const arrow = event.target.closest('.pshoot__arrow');
          if (arrow) this.step(Number(arrow.dataset.dir));
        };
        this.onKey = (event) => {
          if (event.target !== this.track) return;
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(event.key === 'ArrowRight' ? 1 : -1);
          }
        };

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('keydown', this.onKey);
        this.addEventListener('click', this.onClick);
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.resizeObserver.observe(this.track);
        this.update();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('keydown', this.onKey);
        this.removeEventListener('click', this.onClick);
        this.resizeObserver?.disconnect();
        if (this.raf) cancelAnimationFrame(this.raf);
      }

      gutter() {
        return parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      }

      /* Left edge of every item in the track's scroll coordinates. */
      positions() {
        const base = this.track.getBoundingClientRect().left - this.track.scrollLeft;
        return this.items.map((item) => item.getBoundingClientRect().left - base);
      }

      scrollToX(x) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.track.scrollTo({
          left: Math.max(0, Math.min(max, x)),
          behavior: reducedMotion() ? 'auto' : 'smooth',
        });
      }

      step(dir) {
        const edge = this.track.scrollLeft + this.gutter();
        const positions = this.positions();
        let target =
          dir > 0 ? positions.find((p) => p > edge + 2) : positions.reverse().find((p) => p < edge - 2);
        if (target === undefined) target = dir > 0 ? this.track.scrollWidth : 0;
        this.scrollToX(target - this.gutter());
      }

      scrollToItem(item) {
        const index = this.items.indexOf(item);
        if (index < 0) return;
        this.scrollToX(this.positions()[index] - this.gutter());
      }

      update() {
        const x = this.track.scrollLeft;
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (this.prev) this.prev.hidden = x <= 2;
        if (this.next) this.next.hidden = x >= max - 2;

        if (this.current) {
          let index = 0;
          if (x >= max - 2) {
            index = this.items.length - 1;
          } else {
            const edge = x + this.gutter();
            const positions = this.positions();
            positions.forEach((p, i) => {
              if (p <= edge + 2) index = i;
            });
          }
          this.current.textContent = pad2(index + 1);
        }
      }
    }
    customElements.define('photoshoot-strip', PhotoshootStrip);

    /* Theme editor: selecting a photo block scrolls its strip to it. */
    document.addEventListener('shopify:block:select', (event) => {
      const item = document.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      const strip = item && item.closest('photoshoot-strip');
      if (strip && typeof strip.scrollToItem === 'function') strip.scrollToItem(item);
    });
  }

  /* Videos loop silently; clicking toggles play/pause (moving content must be pausable), and
     reduced-motion users get them paused. Every section includes this file, so register once —
     one listener per include would toggle N times per click and cancel out when N is even. */
  if (!window.pshootVideoInit) {
    window.pshootVideoInit = true;
    document.addEventListener('click', (event) => {
      const video = event.target.closest('video.pshoot__media');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });
    const pauseForReducedMotion = () => {
      if (!reducedMotion()) return;
      document.querySelectorAll('video.pshoot__media').forEach((video) => video.pause());
    };
    document.addEventListener('DOMContentLoaded', pauseForReducedMotion);
    document.addEventListener('shopify:section:load', pauseForReducedMotion);
  }

  if (!customElements.get('photoshoot-index')) {
    class PhotoshootIndex extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.pshoot-index__list');
        this.section = this.closest('.shopify-section') || this;

        this.rebuild = () => setTimeout(() => this.build(), 0);
        this.editorEvents = ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'];
        this.editorEvents.forEach((name) => document.addEventListener(name, this.rebuild));

        this.onClick = (event) => this.jump(event);
        this.addEventListener('click', this.onClick);

        this.setHeight = () =>
          document.documentElement.style.setProperty('--pshoot-index-h', `${this.section.offsetHeight}px`);
        this.resizeObserver = new ResizeObserver(this.setHeight);
        this.resizeObserver.observe(this.section);

        this.build();
      }

      disconnectedCallback() {
        this.editorEvents?.forEach((name) => document.removeEventListener(name, this.rebuild));
        this.removeEventListener('click', this.onClick);
        this.resizeObserver?.disconnect();
        this.observer?.disconnect();
        document.documentElement.style.removeProperty('--pshoot-index-h');
      }

      build() {
        if (!this.list) return;
        const shoots = Array.from(document.querySelectorAll('[data-pshoot]:not([data-index-hidden])'));

        /* Two shoots with the same title get the same anchor from Liquid; make later ones unique. */
        const seen = new Set();
        document.querySelectorAll('[data-pshoot]').forEach((shoot) => {
          let id = shoot.id;
          for (let n = 2; seen.has(id); n++) id = `${shoot.id}-${n}`;
          shoot.id = id;
          seen.add(id);
        });

        this.links = new Map();
        this.list.replaceChildren(
          ...shoots.map((shoot) => {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.className = 'pshoot-index__link';
            a.href = `#${shoot.id}`;
            a.textContent = shoot.dataset.title || '';
            li.append(a);
            this.links.set(shoot, a);
            return li;
          })
        );

        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(entry.target);
            });
          },
          { rootMargin: '-40% 0px -55% 0px' }
        );
        shoots.forEach((shoot) => this.observer.observe(shoot));
      }

      setActive(shoot) {
        this.links.forEach((link, key) => {
          if (key === shoot) {
            link.setAttribute('aria-current', 'true');
            const left = link.parentElement.offsetLeft;
            const right = left + link.parentElement.offsetWidth;
            if (left < this.list.scrollLeft || right > this.list.scrollLeft + this.list.clientWidth) {
              this.list.scrollTo({ left: left - 16, behavior: reducedMotion() ? 'auto' : 'smooth' });
            }
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }

      jump(event) {
        const link = event.target.closest('a.pshoot-index__link');
        if (!link) return;
        const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (!target) return;
        event.preventDefault();

        const y = target.getBoundingClientRect().top + window.scrollY;
        /* Dawn's "on scroll up" header reappears when scrolling up, so leave room for it then. */
        const goingUp = y < window.scrollY;
        const alwaysSticky = document.querySelector(
          'sticky-header[data-sticky-type="always"], sticky-header[data-sticky-type="reduce-logo-size"]'
        );
        const header = goingUp || alwaysSticky
          ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0
          : 0;
        const offset = this.section.offsetHeight + header - 1;

        window.scrollTo({ top: Math.max(0, y - offset), behavior: reducedMotion() ? 'auto' : 'smooth' });
        history.replaceState(null, '', link.hash);
        this.setActive(target);
      }
    }
    customElements.define('photoshoot-index', PhotoshootIndex);
  }
})();
